import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, SelectPicker } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { PointAccountList as PointAccountListModel, PointAccountPaginationFilter, PointAccountStatusList, PointAccountTargetType } from '@/models/point-account.model';
import { getPagedPointAccounts } from '@/services/point-account.service';
import { getPointTypesLookup } from '@/services/point-type.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import PageContent from '@/components/PageContent';
import FetchingSelect from '@/components/FetchingSelect';
import { jsDateToZone } from '@/utils/date-time';
import PointAccountStatusTag from './point-account-status-tag';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface PointAccountListProps {
  /** When set, only accounts assigned to this target are listed. */
  targetType?: PointAccountTargetType;
  targetId?: number;
  toolbarExtra?: React.ReactNode;
  reloadKey?: number;
}

const PointAccountList: React.FC<PointAccountListProps> = ({ targetType, targetId, toolbarExtra, reloadKey }) => {
  const [filterReq, setFilterReq] = useState<PointAccountPaginationFilter>(new PointAccountPaginationFilter());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<PointAccountListModel[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq, reloadKey]);

  const fetchData = async (req: PointAccountPaginationFilter) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<PointAccountListModel> = await getPagedPointAccounts({
        ...req,
        targetType: targetType ?? req.targetType,
        targetId: targetId ?? req.targetId,
      });
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ ...pre, pageNumber: result.pageNumber, totalRecords: result.totalRecords, pageSize: result.pageSize }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching point accounts: ${errorModel?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({ ...pre, orderBy: sortColumn, isAscending: sortType == 'asc' }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        {toolbarExtra ?? (
          <Button appearance="primary" onClick={() => nav('/point-accounts/new')}>
            Add Point Account
          </Button>
        )}
        <Stack spacing={6}>
          <FetchingSelect
            label="Point Type"
            fetchData={(text, page) => getPointTypesLookup(text, page)}
            value={filterReq.pointTypeId}
            onChange={val => setFilterReq(pre => ({ ...pre, pointTypeId: val ?? undefined }))}
          />
          <SelectPicker
            label="Status"
            data={PointAccountStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={val => setFilterReq(pre => ({ ...pre, status: val ?? undefined }))}
          />
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={val => setFilterReq(pre => ({ ...pre, keyword: val }))}
          />
        </Stack>
      </Stack>

      <Table
        height={targetId ? 400 : Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.4} align="center" sortable>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="left" fullText>
          <HeaderCell>Point Type</HeaderCell>
          <Cell dataKey="pointTypeName" />
        </Column>
        <Column flexGrow={0.8} align="center" sortable>
          <HeaderCell>Status</HeaderCell>
          <Cell dataKey="status">
            {(row: PointAccountListModel) => <PointAccountStatusTag status={row.status} />}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Starts At</HeaderCell>
          <Cell dataKey="startsAt" fullText>{(row: PointAccountListModel) => jsDateToZone(row.startsAt)}</Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Ends At</HeaderCell>
          <Cell dataKey="endsAt" fullText>{(row: PointAccountListModel) => jsDateToZone(row.endsAt)}</Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Created At</HeaderCell>
          <Cell dataKey="createdAt" fullText>{(row: PointAccountListModel) => jsDateToZone(row.createdAt)}</Cell>
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(row: PointAccountListModel) => (
              <IconButton onClick={() => nav(`/point-accounts/${row.id}`)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={val => setFilterReq(pre => ({ ...pre, pageNumber: val }))}
        onChangeLimit={val => setFilterReq(pre => ({ ...pre, pageSize: val }))}
      />
    </PageContent>
  );
};

export default PointAccountList;
